import React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentTurnPlayer, selectRollsLeft, selectIsMyTurn, selectHasRolledYet, selectDiceRoll, selectPlayerAtLake, selectPlayerAtCityCenter, selectPlayerDataAtRollTime } from '../../redux/gameSlice';
import DiceArea from './DiceArea';
import ActionButtons from './ActionButtons';
import { selectIsRollingDice } from '../../redux/animationSlice';
import { getMaxPlayerHealth } from '../../playerUtils';
import { Trans } from '@lingui/macro';

function getRollExplanation(dice, curTurnPlayerMaxHealth, curTurnPlayerHealth, isCurTurnPlayerInTown, isAnyPlayerInTown, isRolling, rollsLeft) {
  let accumulatedRoll = [
    0, // 1
    0, // 2
    0, // 3
    0, // Heart: 4,
    0, // Damage: 5
    0 // Energy: 6
  ];

  for (let i = 0; i < 8; ++i) {
    if (dice[i] === 0) {
      continue;
    }

    accumulatedRoll[dice[i] - 1]++;
  }

  const rollSum = accumulatedRoll.reduce((pre, v) => v + pre, 0);
  if (rollSum === 0) {
    return {
      tooltip: "",
      diceHasEffect: [false, false, false, false, false, false, false, false]
    };
  }

  let starCount = 0;
  for (let i = 0; i < 3; ++i) {
    let val = accumulatedRoll[i];
    if (i === 0 || i === 1 || i === 2) {
      // Stars
      if (val >= 3) {
        starCount += i + 1 + val - 3;
      }
    }
  }

  let effects = [];
  let warning = [];
  if (starCount) {
    effects.push(<li key="stars"><Trans>Receive {starCount} stars</Trans></li>);
  }

  let damageCount = accumulatedRoll[4];
  if (damageCount) {
    if (isCurTurnPlayerInTown) {
      effects.push(<li key="damage"><Trans>Deal {damageCount} damage to monsters outside the town</Trans></li>);
    } else if (isAnyPlayerInTown) {
      effects.push(<li key="damage"><Trans>Deal {damageCount} damage to monsters in the town</Trans></li>);
    } else {
      warning.push(<li key="damage-warn"><Trans>No damage dealt, as there are no monsters in town!</Trans></li>);
    }
  }

  let energyCount = accumulatedRoll[5];
  if (energyCount) {
    effects.push(<li key="energy"><Trans>Receive {energyCount} energy</Trans></li>);
  }

  const healFor = accumulatedRoll[3];
  const healthApplied = Math.min(curTurnPlayerMaxHealth - curTurnPlayerHealth, healFor);
  if (healFor) {
    if (healthApplied === 0) {
      warning.push(<li key="health-err"><Trans>The monster is already full health</Trans></li>);
    }
    else if (isCurTurnPlayerInTown) {
      warning.push(<li key="health-err"><Trans>Monsters in town don't recover health</Trans></li>);
    }
    else {
      effects.push(<li key="health"><Trans>Regain {healthApplied} heart</Trans></li>);

      if (healthApplied !== healFor) {
        warning.push(<li key="health-err"><Trans>Not all hearts here have an effect</Trans></li>);
      }
    }
  }

  const rolledAnyNumbers = accumulatedRoll[0] > 0 || accumulatedRoll[1] > 0 || accumulatedRoll[2] > 0;
  if (starCount === 0 && rolledAnyNumbers === true && rollsLeft > 0) {
    warning.push(<li key='number-warn'><Trans>You need more of the same number to get stars</Trans></li>);
  }

  if (effects.length === 0) {
    effects = <li><Trans>This roll has no effects</Trans></li>;
  }

  let tooltip = [<ul key='effect-list'>{effects}</ul>];
  if (warning.length > 0) {
    tooltip.push(<br key='linebreak-warning' />);
    tooltip.push(<p key='warning'> <i className="fas fa-exclamation-triangle"></i>&nbsp;&nbsp;<b><Trans>Warnings</Trans></b></p>);
    tooltip.push(<ul key='warning-detail'>{warning}</ul>);
  }

  const diceHasEffect = dice.map((i, idx) => {
    if (isRolling
      || (i === 4 && (healFor === 0 || healthApplied === 0 || isCurTurnPlayerInTown))
      || (i <= 3 && accumulatedRoll[i - 1] < 3)
      || (i === 5 && !isAnyPlayerInTown && !isCurTurnPlayerInTown)) {
      return false;
    }

    return true;
  });

  return {
    tooltip: tooltip,
    diceHasEffect: diceHasEffect
  };
}

export default function DiceRoll(props) {
  const isMyTurn = useSelector(selectIsMyTurn);
  const curTurnPlayer = useSelector(selectCurrentTurnPlayer);
  const rollsLeft = useSelector(selectRollsLeft);
  const isRolling = useSelector(selectIsRollingDice);
  const hasRolledDice = useSelector(selectHasRolledYet);
  const dice = useSelector(selectDiceRoll);
  const playerIdAtLake = useSelector(selectPlayerAtLake);
  const playerIdAtCityCenter = useSelector(selectPlayerAtCityCenter);
  const playerDataAtRollTime = useSelector(selectPlayerDataAtRollTime);
  const rollCompleted = rollsLeft === 0 && !isRolling;

  const curTurnPlayerMaxHealth = getMaxPlayerHealth(curTurnPlayer);
  let curTurnPlayerHealth = curTurnPlayer.health;
  let isCurTurnPlayerInTown = [playerIdAtLake, playerIdAtCityCenter].includes(curTurnPlayer.id);
  let isAnyPlayerInTown = playerIdAtCityCenter !== 0 && playerIdAtCityCenter !== 0;
  if (rollCompleted && playerDataAtRollTime) {
    // If the roll completed, we need to use the information from *before* the roll happened to properly
    // give warnings and consider which players are being damaged
    curTurnPlayerHealth = playerDataAtRollTime.health;
    isCurTurnPlayerInTown = playerDataAtRollTime.town_players.includes(curTurnPlayer.id);
    isAnyPlayerInTown = playerDataAtRollTime.town_players[0] !== 0 || playerDataAtRollTime.town_players[1] !== 0;
  }

  const rollData = getRollExplanation(dice, curTurnPlayerMaxHealth, curTurnPlayerHealth, isCurTurnPlayerInTown, isAnyPlayerInTown, isRolling, rollsLeft);

  let explanation = "";
  let rollsLeftTitle = "";
  let tooltip = "";
  if (isMyTurn) {
    if (rollCompleted === false) {
      if (hasRolledDice) {
        explanation = <Trans>Select the dice you want to roll or accept the current set and click to continue your turn</Trans>;
        tooltip = isRolling ? <p><Trans>Dice are being rolled</Trans></p> : rollData.tooltip;
      } else {
        explanation = <Trans>It's your turn to roll the dice</Trans>;
        tooltip = <p><Trans>No dice rolled yet</Trans></p>;
      }
    } else {
      explanation = <Trans>This has been the dice roll for your turn</Trans>;
      tooltip = rollData.tooltip;
    }
  } else {
    if (hasRolledDice) {
      if (rollsLeft > 0) {
        rollsLeftTitle = <>({rollsLeft === 1 ? <Trans>1 roll left</Trans> : <Trans>{rollsLeft} rolls left</Trans>})</>;
      }

      explanation = <Trans>{curTurnPlayer.name} is rolling the dice!</Trans>;
      tooltip = isRolling ? <p><Trans>Dice are being rolled</Trans></p> : rollData.tooltip;
    } else {
      explanation = <Trans>It's {curTurnPlayer.name}'s turn to roll the dice!</Trans>;
      tooltip = <p><Trans>No dice rolled yet</Trans></p>;
    }
  }

  return <div id="current-dice-roll">
    <span className="tooltip-wrap" aligned="left">
      <span className="tooltip">
        <p><b>{rollCompleted ? <Trans>Dice roll effects <span style={{ color: "#ff6c74" }}>were</span></Trans> : <Trans>Dice roll effects</Trans>}</b></p>
        {tooltip}
      </span>
    </span>
    <div className="spec-header">
      <p className="content-title"><Trans>Current dice roll</Trans> {rollsLeftTitle}</p>
      <p className="content-explanation">{explanation}</p>
    </div>
    <div className="dice-container" >
      <DiceArea diceHasEffect={rollData.diceHasEffect} />
      <div id="turn-action-buttons">
        <ActionButtons />
      </div>
    </div>
  </div >;
}
